/* ==========================================================================
   KUTE NEST — theme.js
   Vanilla JS, no dependencies. Everything initializes from data attributes
   so sections stay reorderable in the theme editor.
   ========================================================================== */
(function () {
  'use strict';

  var config = window.KuteNest || {};

  /* ------------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var moneyFormat = format || config.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var formatted = moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, dollars + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, dollars)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0] + ',' + parts[1]);
    return formatted.replace(/<[^>]*>/g, '');
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  /* ------------------------------------------------------------------------
     Drawers (mobile menu, cart) — open/close, body lock, focus management
     ------------------------------------------------------------------------ */
  var Drawers = {
    open: function (name) {
      var drawer = document.querySelector('[data-drawer="' + name + '"]');
      if (!drawer) return;
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      var focusable = drawer.querySelector('input, button:not([data-drawer-close]), a');
      var closeBtn = drawer.querySelector('[data-drawer-close]');
      (closeBtn || focusable) && (closeBtn || focusable).focus({ preventScroll: true });
      this._active = drawer;
    },
    close: function (drawer) {
      drawer = drawer || this._active;
      if (!drawer) return;
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      this._active = null;
    },
    init: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-menu-toggle]')) self.open('menu');
        if (e.target.closest('[data-cart-toggle]')) {
          e.preventDefault();
          self.open('cart');
        }
        var closer = e.target.closest('[data-drawer-close]');
        if (closer) self.close(closer.closest('[data-drawer]'));
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          self.close();
          SearchOverlay.close();
        }
      });
    }
  };

  /* ------------------------------------------------------------------------
     Sticky header — border/blur after scroll
     ------------------------------------------------------------------------ */
  function initStickyHeader() {
    var header = document.querySelector('[data-sticky-header]');
    if (!header) return;
    var ticking = false;
    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------------
     Search overlay + predictive search
     ------------------------------------------------------------------------ */
  var SearchOverlay = {
    el: null,
    init: function () {
      this.el = document.querySelector('[data-search-overlay]');
      if (!this.el) return;
      var self = this;
      var input = this.el.querySelector('[data-search-input]');
      var results = this.el.querySelector('[data-search-results]');

      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-search-toggle]')) self.openOverlay(input);
        if (e.target.closest('[data-search-close]')) self.close();
      });

      this.el.addEventListener('click', function (e) {
        if (e.target === self.el) self.close();
      });

      if (input && results) {
        input.addEventListener('input', debounce(function () {
          var q = input.value.trim();
          if (q.length < 2) { results.innerHTML = ''; return; }
          fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6')
            .then(function (r) { return r.json(); })
            .then(function (data) {
              var products = (data.resources && data.resources.results && data.resources.results.products) || [];
              results.innerHTML = products.map(function (p) {
                return '<a class="search-overlay__result" href="' + p.url + '">' +
                  (p.featured_image && p.featured_image.url
                    ? '<img src="' + p.featured_image.url + '&width=128" width="64" height="64" loading="lazy" alt="">'
                    : '') +
                  '<span><span class="search-overlay__result-title">' + escapeHtml(p.title) + '</span>' +
                  '<span class="search-overlay__result-price"> ' + escapeHtml(p.price ? formatMoney(parseFloat(p.price) * 100) : '') + '</span></span>' +
                  '</a>';
              }).join('');
            })
            .catch(function () { /* predictive search is progressive enhancement */ });
        }, 250));
      }
    },
    openOverlay: function (input) {
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      if (input) input.focus({ preventScroll: true });
    },
    close: function () {
      if (!this.el || this.el.getAttribute('aria-hidden') === 'true') return;
      this.el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
    }
  };

  /* ------------------------------------------------------------------------
     Cart — AJAX add/change, drawer re-render, count bump
     ------------------------------------------------------------------------ */
  var Cart = {
    add: function (items) {
      return fetch(config.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      }).then(function (data) {
        return Cart.refresh().then(function () { return data; });
      });
    },
    changeLine: function (line, quantity) {
      return fetch(config.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      }).then(function (r) { return r.json(); }).then(function (cart) {
        // The cart page renders line items server-side — reload to stay in sync
        if (document.body.classList.contains('template-cart')) {
          window.location.reload();
          return cart;
        }
        Cart.render(cart);
        return cart;
      });
    },
    refresh: function () {
      return fetch(config.routes.cart + '.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { Cart.render(cart); return cart; });
    },
    render: function (cart) {
      var counts = document.querySelectorAll('[data-cart-count]');
      counts.forEach(function (el) {
        el.textContent = cart.item_count;
        el.classList.toggle('is-empty', cart.item_count === 0);
        el.classList.add('is-bumped');
        setTimeout(function () { el.classList.remove('is-bumped'); }, 300);
      });

      var drawerCount = document.querySelector('[data-cart-drawer-count]');
      if (drawerCount) drawerCount.textContent = '(' + cart.item_count + ')';

      var itemsEl = document.querySelector('[data-cart-items]');
      var emptyEl = document.querySelector('[data-cart-empty]');
      var footerEl = document.querySelector('[data-cart-footer]');
      var subtotalEl = document.querySelector('[data-cart-subtotal]');

      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      if (emptyEl) emptyEl.classList.toggle('hidden', cart.item_count > 0);
      if (footerEl) footerEl.classList.toggle('hidden', cart.item_count === 0);

      if (itemsEl) {
        itemsEl.classList.toggle('hidden', cart.item_count === 0);
        itemsEl.innerHTML = cart.items.map(function (item, i) {
          var image = item.image
            ? item.image.replace(/(\.[a-z]{3,4})(\?|$)/, '_240x$1$2')
            : '';
          return '<li class="cart-item" data-line="' + (i + 1) + '" data-key="' + item.key + '">' +
            '<a href="' + item.url + '" class="cart-item__media">' +
              (image ? '<img src="' + image + '" width="96" height="96" loading="lazy" alt="">' : '') +
            '</a>' +
            '<div class="cart-item__details">' +
              '<a href="' + item.url + '" class="cart-item__title">' + escapeHtml(item.product_title) + '</a>' +
              (item.variant_title ? '<p class="cart-item__variant">' + escapeHtml(item.variant_title) + '</p>' : '') +
              '<p class="cart-item__price">' + formatMoney(item.final_line_price) + '</p>' +
              '<div class="cart-item__controls">' +
                '<div class="quantity" data-quantity>' +
                  '<button type="button" class="quantity__button tap-target" data-quantity-minus aria-label="Decrease quantity">&minus;</button>' +
                  '<input class="quantity__input" type="number" inputmode="numeric" min="0" value="' + item.quantity + '" data-quantity-input aria-label="Quantity">' +
                  '<button type="button" class="quantity__button tap-target" data-quantity-plus aria-label="Increase quantity">+</button>' +
                '</div>' +
                '<button type="button" class="cart-item__remove" data-cart-remove>Remove</button>' +
              '</div>' +
            '</div>' +
          '</li>';
        }).join('');
      }

      dispatch('cart:updated', cart);
    },
    init: function () {
      document.addEventListener('click', function (e) {
        var item = e.target.closest('.cart-item');
        if (!item) return;
        var line = parseInt(item.dataset.line, 10);
        var input = item.querySelector('[data-quantity-input]');

        if (e.target.closest('[data-cart-remove]')) {
          item.classList.add('is-updating');
          Cart.changeLine(line, 0);
        } else if (e.target.closest('[data-quantity-plus]')) {
          item.classList.add('is-updating');
          Cart.changeLine(line, parseInt(input.value, 10) + 1);
        } else if (e.target.closest('[data-quantity-minus]')) {
          item.classList.add('is-updating');
          Cart.changeLine(line, Math.max(0, parseInt(input.value, 10) - 1));
        }
      });

      document.addEventListener('change', function (e) {
        var input = e.target.closest('.cart-item [data-quantity-input]');
        if (!input) return;
        var item = input.closest('.cart-item');
        item.classList.add('is-updating');
        Cart.changeLine(parseInt(item.dataset.line, 10), Math.max(0, parseInt(input.value, 10) || 0));
      });
    }
  };

  /* ------------------------------------------------------------------------
     Product form — variant matching, price/media updates, AJAX add to cart
     ------------------------------------------------------------------------ */
  function initProductForms() {
    document.querySelectorAll('[data-product-form]').forEach(function (container) {
      var jsonEl = container.querySelector('[data-product-json]');
      if (!jsonEl) return;
      var product = JSON.parse(jsonEl.textContent);
      var form = container.querySelector('form[data-form]');
      var idInput = container.querySelector('[data-variant-id]');
      var priceEl = container.querySelector('[data-price]');
      var comparePriceEl = container.querySelector('[data-compare-price]');
      var buttons = container.querySelectorAll('[data-atc-button]');
      var stickyBar = document.querySelector('[data-sticky-atc]');

      function selectedOptions() {
        var opts = [];
        container.querySelectorAll('[data-option-index]').forEach(function (group) {
          var checked = group.querySelector('input:checked');
          if (checked) opts[parseInt(group.dataset.optionIndex, 10)] = checked.value;
        });
        return opts;
      }

      function findVariant(opts) {
        return product.variants.find(function (v) {
          return v.options.every(function (val, i) { return val === opts[i]; });
        });
      }

      function updateUI(variant) {
        if (!variant) {
          buttons.forEach(function (b) {
            b.disabled = true;
            b.querySelector('[data-atc-text]').textContent = config.strings.unavailable;
          });
          return;
        }
        idInput.value = variant.id;

        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (comparePriceEl) {
          if (variant.compare_at_price > variant.price) {
            comparePriceEl.textContent = formatMoney(variant.compare_at_price);
            comparePriceEl.classList.remove('hidden');
          } else {
            comparePriceEl.classList.add('hidden');
          }
        }

        buttons.forEach(function (b) {
          b.disabled = !variant.available;
          b.querySelector('[data-atc-text]').textContent =
            variant.available ? config.strings.addToCart : config.strings.soldOut;
        });

        if (stickyBar) {
          var sp = stickyBar.querySelector('[data-sticky-price]');
          if (sp) sp.textContent = formatMoney(variant.price);
        }

        // Scroll gallery to the variant's media
        if (variant.featured_media) {
          var slide = document.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
          if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }

        // Update option label values ("Color — Tricorn Black")
        container.querySelectorAll('[data-option-index]').forEach(function (group) {
          var label = group.querySelector('[data-option-value]');
          var checked = group.querySelector('input:checked');
          if (label && checked) label.textContent = checked.value;
        });

        // History: keep URL shareable per-variant
        if (window.history.replaceState && product.handle) {
          var url = window.location.pathname + '?variant=' + variant.id;
          window.history.replaceState({}, '', url);
        }

        dispatch('variant:changed', { variant: variant, product: product });
      }

      container.addEventListener('change', function (e) {
        if (!e.target.closest('[data-option-index]')) return;
        updateUI(findVariant(selectedOptions()));
      });

      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var qtyInput = form.querySelector('[data-main-quantity]');
          var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
          var btn = form.querySelector('[data-atc-button]');
          var label = btn.querySelector('[data-atc-text]');
          var original = label.textContent;

          btn.classList.add('is-loading');
          label.textContent = config.strings.adding;

          Cart.add([{ id: parseInt(idInput.value, 10), quantity: qty }])
            .then(function () {
              label.textContent = config.strings.added;
              Drawers.open('cart');
              setTimeout(function () {
                label.textContent = original;
                btn.classList.remove('is-loading');
              }, 1400);
            })
            .catch(function (err) {
              label.textContent = (err && err.description) || original;
              btn.classList.remove('is-loading');
              setTimeout(function () { label.textContent = original; }, 2500);
            });
        });
      }

      // Quantity stepper on the form
      container.addEventListener('click', function (e) {
        var qty = e.target.closest('[data-quantity]');
        if (!qty || e.target.closest('.cart-item')) return;
        var input = qty.querySelector('[data-quantity-input]');
        if (e.target.closest('[data-quantity-plus]')) input.value = parseInt(input.value, 10) + 1;
        if (e.target.closest('[data-quantity-minus]')) input.value = Math.max(1, parseInt(input.value, 10) - 1);
      });
    });
  }

  /* ------------------------------------------------------------------------
     Product gallery — dots sync on mobile, thumb navigation on desktop
     ------------------------------------------------------------------------ */
  function initGalleries() {
    document.querySelectorAll('[data-gallery]').forEach(function (gallery) {
      var track = gallery.querySelector('[data-gallery-track]');
      var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-slide]'));
      var dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-dot]'));
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
      if (!track || !slides.length) return;

      function setActive(index) {
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
        thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === index); });
      }

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            setActive(slides.indexOf(entry.target));
          }
        });
      }, { root: track, threshold: 0.6 });

      slides.forEach(function (s) { observer.observe(s); });

      function goTo(index) {
        slides[index] && slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        setActive(index);
      }

      dots.forEach(function (d, i) { d.addEventListener('click', function () { goTo(i); }); });
      thumbs.forEach(function (t, i) { t.addEventListener('click', function () { goTo(i); }); });
    });
  }

  /* ------------------------------------------------------------------------
     Sticky mobile ATC — appears when the main buy button scrolls offscreen
     ------------------------------------------------------------------------ */
  function initStickyATC() {
    var bar = document.querySelector('[data-sticky-atc]');
    var anchor = document.querySelector('[data-atc-anchor]');
    if (!bar || !anchor) return;

    var observer = new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
    }, { threshold: 0 });
    observer.observe(anchor);

    var trigger = bar.querySelector('[data-sticky-submit]');
    if (trigger) {
      trigger.addEventListener('click', function () {
        var form = document.querySelector('[data-product-form] form[data-form]');
        if (form) form.requestSubmit();
      });
    }
  }

  /* ------------------------------------------------------------------------
     Size comparison tool — planter rendered to scale against references
     ------------------------------------------------------------------------ */
  function initSizeCompare() {
    document.querySelectorAll('[data-size-compare]').forEach(function (tool) {
      var planter = tool.querySelector('[data-sc-planter]');
      var refFigure = tool.querySelector('[data-sc-reference]');
      var refLabel = tool.querySelector('[data-sc-ref-label]');
      var stage = tool.querySelector('[data-sc-stage]');
      var buttons = tool.querySelectorAll('[data-sc-ref]');
      if (!planter || !stage) return;

      var productH = parseFloat(tool.dataset.heightInches) || 16;
      var productD = parseFloat(tool.dataset.diameterInches) || 16;

      // Reference objects: height in inches + an SVG silhouette
      var REFERENCES = {
        human: { label: 'Average adult · 5′7″ (67″)', inches: 67, svg: '<svg viewBox="0 0 32 96" fill="currentColor" aria-hidden="true"><circle cx="16" cy="9" r="7"/><rect x="9" y="19" width="14" height="38" rx="6"/><rect x="10" y="55" width="5.2" height="39" rx="2.6"/><rect x="16.8" y="55" width="5.2" height="39" rx="2.6"/></svg>' },
        door: { label: 'Interior door · 80″', inches: 80, svg: '<svg viewBox="0 0 40 96" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="2" y="2" width="36" height="92"/><rect x="8" y="8" width="24" height="34"/><rect x="8" y="50" width="24" height="36"/><circle cx="33" cy="48" r="1.6" fill="currentColor"/></svg>' },
        chair: { label: 'Dining chair · 35″', inches: 35, svg: '<svg viewBox="0 0 48 96" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M10 4v52M38 4v52M10 30h28M10 56h28M12 56l-4 36M36 56l4 36"/></svg>' },
        tree: { label: 'Fiddle leaf fig · 60″', inches: 60, svg: '<svg viewBox="0 0 56 96" fill="currentColor" aria-hidden="true"><ellipse cx="28" cy="22" rx="16" ry="18" opacity="0.85"/><ellipse cx="14" cy="38" rx="11" ry="13" opacity="0.7"/><ellipse cx="42" cy="40" rx="11" ry="13" opacity="0.7"/><ellipse cx="28" cy="50" rx="12" ry="12" opacity="0.8"/><rect x="26" y="56" width="4" height="40" rx="2"/></svg>' }
      };

      function render(key) {
        var ref = REFERENCES[key] || REFERENCES.human;
        var stageH = stage.clientHeight - 40; // breathing room above tallest figure
        var maxInches = Math.max(ref.inches, productH);
        var pxPerInch = stageH / maxInches;

        if (refFigure) {
          refFigure.innerHTML = ref.svg;
          var svg = refFigure.querySelector('svg');
          if (svg) {
            svg.style.height = (ref.inches * pxPerInch) + 'px';
          }
        }
        if (refLabel) refLabel.textContent = ref.label;

        planter.style.height = (productH * pxPerInch) + 'px';
        planter.style.width = (productD * pxPerInch) + 'px';

        buttons.forEach(function (b) {
          b.classList.toggle('is-active', b.dataset.scRef === key);
        });
      }

      buttons.forEach(function (b) {
        b.addEventListener('click', function () { render(b.dataset.scRef); });
      });

      window.addEventListener('resize', debounce(function () {
        var active = tool.querySelector('[data-sc-ref].is-active');
        render(active ? active.dataset.scRef : 'human');
      }, 200));

      render('human');
    });
  }

  /* ------------------------------------------------------------------------
     Color Room Matcher — client-side only; image never leaves the browser.
     Downsamples the upload to a canvas, finds dominant muted tones, and
     recommends the closest planter color.
     ------------------------------------------------------------------------ */
  function initRoomMatcher() {
    document.querySelectorAll('[data-room-matcher]').forEach(function (matcher) {
      var input = matcher.querySelector('[data-rm-input]');
      var dropzone = matcher.querySelector('[data-rm-dropzone]');
      var preview = matcher.querySelector('[data-rm-preview]');
      var canvas = matcher.querySelector('[data-rm-canvas]');
      var paletteEl = matcher.querySelector('[data-rm-palette]');
      var matchEl = matcher.querySelector('[data-rm-match]');
      if (!input || !dropzone || !canvas) return;

      var BRAND_COLORS = [
        { name: 'Tricorn Black', handle: 'tricorn-black', rgb: [44, 44, 44], reason: 'grounds bright, high-contrast rooms and anchors light floors' },
        { name: 'Santorini White', handle: 'santorini-white', rgb: [245, 242, 236], reason: 'keeps airy rooms feeling open and lifts darker corners' },
        { name: 'Kilim Beige', handle: 'kilim-beige', rgb: [212, 201, 184], reason: 'blends with oak, linen, and warm neutral palettes' },
        { name: 'Taupe', handle: 'taupe', rgb: [155, 142, 126], reason: 'bridges warm woods and gray tones in layered rooms' },
        { name: 'Light French Gray', handle: 'light-french-gray', rgb: [197, 197, 197], reason: 'complements cool palettes, concrete, and black metal accents' },
        { name: 'Blue Sage', handle: 'blue-sage', rgb: [127, 156, 151], reason: 'echoes greenery and adds a soft natural accent to neutral rooms' }
      ];

      function distance(a, b) {
        // Weighted RGB distance — human eyes weigh green most
        var dr = a[0] - b[0], dg = a[1] - b[1], db = a[2] - b[2];
        return Math.sqrt(2 * dr * dr + 4 * dg * dg + 3 * db * db);
      }

      function analyze(file) {
        var img = new Image();
        var url = URL.createObjectURL(file);
        img.onload = function () {
          var ctx = canvas.getContext('2d', { willReadFrequently: true });
          var scale = Math.min(1, 480 / img.width);
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);

          // Quantize to a coarse grid and bucket-count
          var data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          var buckets = {};
          for (var i = 0; i < data.length; i += 16) { // sample every 4th pixel
            var r = data[i], g = data[i + 1], b = data[i + 2];
            var max = Math.max(r, g, b), min = Math.min(r, g, b);
            // Skip near-pure white/black noise and oversaturated pixels —
            // room tone lives in the mid, muted range
            if (max > 250 || max < 18 || (max - min) > 120) continue;
            var key = (r >> 5) + ',' + (g >> 5) + ',' + (b >> 5);
            if (!buckets[key]) buckets[key] = { count: 0, r: 0, g: 0, b: 0 };
            buckets[key].count++;
            buckets[key].r += r; buckets[key].g += g; buckets[key].b += b;
          }

          var top = Object.keys(buckets).map(function (k) {
            var bk = buckets[k];
            return { count: bk.count, rgb: [Math.round(bk.r / bk.count), Math.round(bk.g / bk.count), Math.round(bk.b / bk.count)] };
          }).sort(function (a, b) { return b.count - a.count; }).slice(0, 4);

          if (!top.length) return;

          // Show extracted palette
          if (paletteEl) {
            paletteEl.innerHTML = top.map(function (t) {
              return '<span class="room-matcher__palette-chip" style="background: rgb(' + t.rgb.join(',') + ')"></span>';
            }).join('');
          }

          // Score each brand color against the weighted dominant palette.
          // A close match = harmonious; a moderate-contrast match also works
          // (planter should read against the wall, not vanish into it).
          var best = null;
          BRAND_COLORS.forEach(function (c) {
            var score = 0;
            top.forEach(function (t, idx) {
              var d = distance(c.rgb, t.rgb);
              var weight = t.count * (1 - idx * 0.15);
              // Sweet spot: harmonize without disappearing
              var fit = d < 60 ? 0.6 : (d < 260 ? 1 : 260 / d);
              score += weight * fit;
            });
            if (!best || score > best.score) best = { color: c, score: score };
          });

          if (matchEl && best) {
            matchEl.innerHTML =
              '<span class="swatch swatch--' + best.color.handle + '" aria-hidden="true"></span>' +
              '<span><span class="room-matcher__match-name">' + best.color.name + '</span>' +
              '<span class="room-matcher__match-reason"> — ' + best.color.reason + '.</span></span>';
          }

          if (preview) preview.classList.add('is-visible');
          dropzone.classList.add('hidden');
        };
        img.src = url;
      }

      dropzone.addEventListener('click', function () { input.click(); });
      dropzone.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); }
      });
      input.addEventListener('change', function () {
        if (input.files && input.files[0]) analyze(input.files[0]);
      });
      ['dragover', 'dragleave', 'drop'].forEach(function (evt) {
        dropzone.addEventListener(evt, function (e) {
          e.preventDefault();
          dropzone.classList.toggle('is-dragover', evt === 'dragover');
          if (evt === 'drop' && e.dataTransfer.files[0]) analyze(e.dataTransfer.files[0]);
        });
      });

      var reset = matcher.querySelector('[data-rm-reset]');
      if (reset) {
        reset.addEventListener('click', function () {
          preview.classList.remove('is-visible');
          dropzone.classList.remove('hidden');
          input.value = '';
        });
      }
    });
  }

  /* ------------------------------------------------------------------------
     Bundle builder — check items, live total, add all to cart
     ------------------------------------------------------------------------ */
  function initBundleBuilder() {
    document.querySelectorAll('[data-bundle]').forEach(function (bundle) {
      var totalEl = bundle.querySelector('[data-bundle-total]');
      var addBtn = bundle.querySelector('[data-bundle-add]');

      function selectedItems() {
        var items = [];
        bundle.querySelectorAll('[data-bundle-item]').forEach(function (row) {
          var check = row.querySelector('[data-bundle-check]');
          if (!check || !check.checked) return;
          var select = row.querySelector('[data-bundle-variant]');
          var id = select ? parseInt(select.value, 10) : parseInt(check.value, 10);
          var price = select
            ? parseInt(select.options[select.selectedIndex].dataset.price, 10)
            : parseInt(check.dataset.price, 10);
          items.push({ id: id, price: price || 0 });
        });
        return items;
      }

      function updateTotal() {
        var items = selectedItems();
        var total = items.reduce(function (sum, i) { return sum + i.price; }, 0);
        if (totalEl) totalEl.textContent = formatMoney(total);
        if (addBtn) addBtn.disabled = items.length === 0;
      }

      bundle.addEventListener('change', function (e) {
        var row = e.target.closest('[data-bundle-item]');
        if (row && e.target.matches('[data-bundle-variant]')) {
          var priceEl = row.querySelector('[data-bundle-item-price]');
          var opt = e.target.options[e.target.selectedIndex];
          if (priceEl && opt) priceEl.textContent = formatMoney(parseInt(opt.dataset.price, 10));
        }
        updateTotal();
      });

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          var items = selectedItems().map(function (i) { return { id: i.id, quantity: 1 }; });
          if (!items.length) return;
          addBtn.classList.add('is-loading');
          Cart.add(items).then(function () {
            addBtn.classList.remove('is-loading');
            Drawers.open('cart');
          }).catch(function () {
            addBtn.classList.remove('is-loading');
          });
        });
      }

      updateTotal();
    });
  }

  /* ------------------------------------------------------------------------
     Delivery ETA — business-day range, honors dispatch cutoff
     ------------------------------------------------------------------------ */
  function initDeliveryETA() {
    var els = document.querySelectorAll('[data-delivery-eta]');
    if (!els.length) return;

    var delivery = config.delivery || { minDays: 3, maxDays: 7, cutoffHour: '14' };

    function addBusinessDays(start, days) {
      var d = new Date(start);
      var added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        var day = d.getDay();
        if (day !== 0 && day !== 6) added++;
      }
      return d;
    }

    var now = new Date();
    var start = new Date(now);
    // Orders after the cutoff dispatch next business day
    if (now.getHours() >= parseInt(delivery.cutoffHour, 10)) {
      start.setDate(start.getDate() + 1);
    }

    var from = addBusinessDays(start, delivery.minDays);
    var to = addBusinessDays(start, delivery.maxDays);
    var fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'short', day: 'numeric' });

    els.forEach(function (el) {
      el.innerHTML = 'Arrives <strong>' + fmt.format(from) + ' – ' + fmt.format(to) + '</strong>';
    });
  }

  /* ------------------------------------------------------------------------
     Collection filters — drawer toggle, auto-apply, sort
     ------------------------------------------------------------------------ */
  function initCollectionFilters() {
    var filters = document.querySelector('[data-collection-filters]');
    var form = document.querySelector('[data-filters-form]');

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-filters-open]') && filters) {
        filters.classList.add('is-open');
        document.body.classList.add('drawer-open');
      }
      if (e.target.closest('[data-filters-close]') && filters) {
        filters.classList.remove('is-open');
        document.body.classList.remove('drawer-open');
      }
    });

    if (form) {
      // Live-update the result count on the apply button as boxes are ticked
      var countBtn = form.querySelector('[data-filters-apply]');
      form.addEventListener('change', debounce(function () {
        if (!countBtn) return;
        var params = new URLSearchParams(new FormData(form));
        fetch(window.location.pathname + '?' + params.toString() + '&section_id=' + (countBtn.dataset.sectionId || ''))
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var count = doc.querySelector('[data-results-count]');
            if (count) countBtn.textContent = count.textContent.trim();
          })
          .catch(function () { /* count preview is enhancement only */ });
      }, 350));
    }

    var sort = document.querySelector('[data-sort-select]');
    if (sort) {
      sort.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    }
  }

  /* ------------------------------------------------------------------------
     Product card swatch hover — swap card image to the variant's media
     ------------------------------------------------------------------------ */
  function initCardSwatches() {
    document.addEventListener('mouseover', handleSwatch);
    document.addEventListener('click', handleSwatch);

    function handleSwatch(e) {
      var swatch = e.target.closest('[data-swatch]');
      if (!swatch) return;
      if (e.type === 'click') e.preventDefault();
      var card = swatch.closest('[data-product-card]');
      if (!card || !swatch.dataset.swatchImage) return;
      var img = card.querySelector('[data-card-image]');
      if (img && img.src !== swatch.dataset.swatchImage) {
        img.removeAttribute('srcset');
        img.src = swatch.dataset.swatchImage;
      }
      card.querySelectorAll('[data-swatch]').forEach(function (s) {
        s.classList.toggle('is-active', s === swatch);
      });
    }
  }

  /* ------------------------------------------------------------------------
     Reveal on scroll — opacity/translate, once per element
     ------------------------------------------------------------------------ */
  function initReveal() {
    var els = document.querySelectorAll('.reveal');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  function init() {
    Drawers.init();
    Cart.init();
    SearchOverlay.init();
    initStickyHeader();
    initProductForms();
    initGalleries();
    initStickyATC();
    initSizeCompare();
    initRoomMatcher();
    initBundleBuilder();
    initDeliveryETA();
    initCollectionFilters();
    initCardSwatches();
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
